'use client';

import React, { useEffect, useMemo, useRef } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import { useParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { DashboardTitleBar } from '../layout/DashboardTitleBar';
import { useBusinessUnitsAll } from '@/hooks/api/useBusinessUnits';
import { useSetCurrentBusinessUnit } from '@/hooks/api/useAuth';

const BusinessUnitDashboardPage: React.FC = () => {
  const { user } = useAuth();
  const { code } = useParams<{ code: string }>();
  const { data: units = [], isLoading } = useBusinessUnitsAll(true);
  const setBu = useSetCurrentBusinessUnit();
  const syncedRef = useRef(false);

  const bu = useMemo(
    () => units.find((u) => u.code === code),
    [units, code],
  );

  useEffect(() => {
    if (!bu?.id || syncedRef.current) return;
    syncedRef.current = true;
    setBu.mutate(bu.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run once per BU id for this mount
  }, [bu?.id]);

  const titleUnitName = bu?.name ?? user?.currentBusinessUnit?.name;

  return (
    <Box>
      <DashboardTitleBar unitName={titleUnitName} />
      {isLoading && <CircularProgress size={28} sx={{ my: 2 }} />}
      {!isLoading && !bu && (
        <Typography color="error">Unknown business unit.</Typography>
      )}
      {bu && (
        <Box sx={{ pt: 0 }}>
          <Typography variant="body1" color="text.secondary">
            This is a placeholder dashboard for this business unit. Operational features will be
            scoped here in a future iteration.
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default BusinessUnitDashboardPage;
